import { getOptions } from '@core/utils/enum';
import { idToForm } from '@core/utils/form';
import { ChasubleBodyType } from '../enum/chasubleBodyType';
import { ChasubleGothicType } from '../enum/chasubleGothicType';
import { ChasublePatchingType } from '../enum/chasublePatchingType';
import { VestmentType, isV1VestmentType } from '../enum/vestmentType';
import type { CollarFlag } from './collar';
import type { SizeMeasurementField } from './size';

export type ChasubleFeatures = {
  available: boolean;
  sizeFields: SizeMeasurementField[];
  panel: boolean;
  centerFinish: boolean;
  panelWidth: boolean;
  patching: boolean;
  gothic: boolean;
  bodyType: boolean;
  roman: boolean;
  collar: boolean;
  collarFlag?: CollarFlag;
  collarDefaults: boolean;
  designTypes: VestmentType[];
  defaultDesignCode: string | null;
};

type OptionalFeature =
  | 'centerFinish'
  | 'panelWidth'
  | 'patching'
  | 'gothic'
  | 'bodyType'
  | 'roman'
  | 'collar'
  | 'collarDefaults'
  | 'defaultDesignCode';

const chasuble = (
  features: Omit<ChasubleFeatures, OptionalFeature> &
    Partial<Pick<ChasubleFeatures, OptionalFeature>>
): ChasubleFeatures => ({
  centerFinish: false,
  panelWidth: false,
  patching: false,
  gothic: false,
  bodyType: false,
  roman: false,
  collar: true,
  collarDefaults: false,
  defaultDesignCode: null,
  ...features,
});

const CHASUBLE_FEATURES: Partial<Record<VestmentType, ChasubleFeatures>> = {
  [VestmentType.ChasubleCenterDesign]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve'],
    panel: true,
    centerFinish: true,
    panelWidth: true,
    designTypes: [VestmentType.ChasubleCenterDesign],
  }),
  [VestmentType.ChasubleCenterPatch]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve'],
    panel: true,
    panelWidth: true,
    patching: true,
    designTypes: [VestmentType.ChasubleCenterDesign],
  }),
  [VestmentType.ChasubleTauOrphrey]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve'],
    panel: true,
    designTypes: [VestmentType.ChasubleTauOrphrey],
  }),
  [VestmentType.ChasubleDirectEmbroideryDesign]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve'],
    panel: false,
    designTypes: [VestmentType.ChasubleDirectEmbroideryDesign],
  }),
  [VestmentType.ChasubleGothicStyle]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve'],
    panel: true,
    gothic: true,
    designTypes: [VestmentType.ChasubleGothicStyle],
  }),
  [VestmentType.ChasubleAlb]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve', 'neck'],
    panel: false,
    bodyType: true,
    collarDefaults: true,
    designTypes: [VestmentType.AlbOverlapping],
  }),
  [VestmentType.ChasublePlain]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve', 'neck'],
    panel: false,
    collarDefaults: true,
    designTypes: [VestmentType.ChasublePlain],
    defaultDesignCode: 'No Design',
  }),
  [VestmentType.ChasubleRoman]: chasuble({
    available: true,
    sizeFields: [],
    panel: true,
    roman: true,
    collar: false,
    designTypes: [VestmentType.ChasubleRoman],
  }),
  [VestmentType.ChasubleV1Orphrey]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve'],
    panel: true,
    centerFinish: true,
    panelWidth: true,
    collarFlag: 'isLegacyOrphrey',
    designTypes: [VestmentType.ChasubleV1Orphrey],
  }),
  [VestmentType.ChasubleV1Plain]: chasuble({
    available: true,
    sizeFields: ['length', 'sleeve', 'neck'],
    panel: false,
    collarFlag: 'isLegacyPlain',
    designTypes: [VestmentType.ChasubleV1Plain],
  }),
};

const CHASUBLE_TYPE_ORDER = Object.keys(CHASUBLE_FEATURES) as VestmentType[];

export function getChasubleFeatures(
  vestmentType?: VestmentType | null
): ChasubleFeatures | undefined {
  return vestmentType ? CHASUBLE_FEATURES[vestmentType] : undefined;
}

export function isChasubleTypeAvailable(vestmentType: VestmentType): boolean {
  return getChasubleFeatures(vestmentType)?.available ?? false;
}

/** Design types whose designs can have presets; mirrors the backend list. */
export const DESIGN_PRESET_TYPES: VestmentType[] = [
  VestmentType.ChasubleCenterDesign,
  VestmentType.ChasubleTauOrphrey,
  VestmentType.ChasubleDirectEmbroideryDesign,
  VestmentType.ChasubleGothicStyle,
  VestmentType.ChasubleRoman,
];

type ChasubleFeatureFlag = {
  [K in keyof ChasubleFeatures]-?: ChasubleFeatures[K] extends boolean
    ? K
    : never;
}[keyof ChasubleFeatures];

/**
 * Fields a preset of a design of this type can set: the features of every
 * non-V1 chasuble type that lists the type's designs, without sizes.
 */
export function getDesignPresetFeatures(
  designType: VestmentType | null | undefined
): ChasubleFeatures | undefined {
  if (!designType || !DESIGN_PRESET_TYPES.includes(designType)) {
    return undefined;
  }
  const sources = Object.entries(CHASUBLE_FEATURES)
    .filter(
      ([type, features]) =>
        !isV1VestmentType(type as VestmentType) &&
        features.designTypes.includes(designType)
    )
    .map(([, features]) => features);
  const any = (flag: ChasubleFeatureFlag) =>
    sources.some((features) => features[flag]);
  return chasuble({
    available: true,
    sizeFields: [],
    panel: any('panel'),
    centerFinish: any('centerFinish'),
    panelWidth: any('panelWidth'),
    patching: any('patching'),
    gothic: any('gothic'),
    bodyType: any('bodyType'),
    roman: any('roman'),
    collar: any('collar'),
    collarDefaults: any('collarDefaults'),
    designTypes: [designType],
  });
}

/** Body, panel, and collar form values shared by chasuble items and design presets. */
export type ChasubleValueFormValues = {
  bodyFabric: string;
  bodyColor: string;
  panelFabric: string;
  panelColor: string;
  patchingType: string;
  thread: string;
  trimming: string;
  gothicType: string;
  galoon: string;
  panelDimension: string;
  liningFabric: string;
  liningColor: string;
  stoleSize: string;
  collar: string;
  collarFabric: string;
  collarColor: string;
};

type ApiRef = { id: number } | null;

export type ChasubleApiValues = {
  bodyFabric?: ApiRef;
  bodyColor?: ApiRef;
  panelFabric?: ApiRef;
  panelColor?: ApiRef;
  patchingType?: string | null;
  thread?: ApiRef;
  trimming?: ApiRef;
  gothicType?: string | null;
  galoon?: ApiRef;
  panelDimension?: string | null;
  liningFabric?: ApiRef;
  liningColor?: ApiRef;
  stoleSize?: string | null;
  collar?: ApiRef;
  collarFabric?: ApiRef;
  collarColor?: ApiRef;
};

export function chasubleValuesFromApi(
  values: ChasubleApiValues
): ChasubleValueFormValues {
  return {
    bodyFabric: idToForm(values.bodyFabric),
    bodyColor: idToForm(values.bodyColor),
    panelFabric: idToForm(values.panelFabric),
    panelColor: idToForm(values.panelColor),
    patchingType: values.patchingType ?? '',
    thread: idToForm(values.thread),
    trimming: idToForm(values.trimming),
    gothicType: values.gothicType ?? '',
    galoon: idToForm(values.galoon),
    panelDimension: values.panelDimension ?? '',
    liningFabric: idToForm(values.liningFabric),
    liningColor: idToForm(values.liningColor),
    stoleSize: values.stoleSize ?? '',
    collar: idToForm(values.collar),
    collarFabric: idToForm(values.collarFabric),
    collarColor: idToForm(values.collarColor),
  };
}

export type ChasubleFeaturesSource =
  | VestmentType
  | ChasubleFeatures
  | null
  | undefined;

function resolveChasubleFeatures(
  source: ChasubleFeaturesSource
): ChasubleFeatures | undefined {
  return typeof source === 'object' && source !== null
    ? source
    : getChasubleFeatures(source);
}

/**
 * New types first in milestone order, then legacy vestments. Vestments of the
 * same rank keep their incoming order.
 */
export function sortChasubleVestments<T extends { type: VestmentType }>(
  vestments: T[]
): T[] {
  const rank = (type: VestmentType) =>
    isV1VestmentType(type)
      ? CHASUBLE_TYPE_ORDER.length
      : CHASUBLE_TYPE_ORDER.indexOf(type);
  return [...vestments].sort((a, b) => rank(a.type) - rank(b.type));
}

export const PATCHING_TYPE_OPTIONS = getOptions(ChasublePatchingType);

export function getPatchingTypeLabel(
  patchingType?: string | null
): string | null {
  return (
    PATCHING_TYPE_OPTIONS.find((option) => option.value === patchingType)
      ?.label ?? null
  );
}

export const GOTHIC_TYPE_OPTIONS = getOptions(ChasubleGothicType);

export function getGothicTypeLabel(gothicType?: string | null): string | null {
  return (
    GOTHIC_TYPE_OPTIONS.find((option) => option.value === gothicType)?.label ??
    null
  );
}

type ChasubleOptionValues = {
  patchingType?: string | null;
  gothicType?: string | null;
};

export type ChasubleMaterialField = 'thread' | 'trimming' | 'galoon';

/** The material field the item's patching or gothic type asks for, if any. */
export function getChasubleMaterialField(
  source: ChasubleFeaturesSource,
  item: ChasubleOptionValues
): ChasubleMaterialField | null {
  const features = resolveChasubleFeatures(source);
  if (features?.patching) {
    switch (item.patchingType) {
      case ChasublePatchingType.BorderEmbroidery:
        return 'thread';
      case ChasublePatchingType.WithTrimmings:
        return 'trimming';
    }
  }
  if (features?.gothic) {
    switch (item.gothicType) {
      case ChasubleGothicType.PlainWithTrimmings:
        return 'trimming';
      case ChasubleGothicType.Galoon:
        return 'galoon';
    }
  }
  return null;
}

/** Gothic galoon panels have no fabric or color. */
export function hasChasublePanelFabric(gothicType?: string | null): boolean {
  return gothicType !== ChasubleGothicType.Galoon;
}

/**
 * Whether the item has a Panel section. Prints keep the panel of legacy plain
 * items, and of items without a known type.
 */
export function showsChasublePanel(
  vestmentType: VestmentType | null | undefined,
  { print = false }: { print?: boolean } = {}
): boolean {
  const features = getChasubleFeatures(vestmentType);
  if (print) {
    return (
      features?.panel !== false ||
      !vestmentType ||
      isV1VestmentType(vestmentType)
    );
  }
  return features?.panel ?? false;
}

/** Whether the item has a Collar section; items without a known type keep it. */
export function showsChasubleCollar(
  vestmentType: VestmentType | null | undefined
): boolean {
  return getChasubleFeatures(vestmentType)?.collar ?? true;
}

export type ChasublePanelField =
  | 'fabric'
  | 'color'
  | 'width'
  | 'length'
  | 'patching'
  | 'trimming'
  | 'gothicType'
  | 'galoon'
  | 'dimension'
  | 'liningFabric'
  | 'liningColor'
  | 'stoleSize';

/**
 * Panel fields in display order, for when the panel is shown. `patching`
 * covers the patching type and its material.
 */
export function getChasublePanelFields(
  source: ChasubleFeaturesSource,
  item: ChasubleOptionValues,
  { print = false }: { print?: boolean } = {}
): ChasublePanelField[] {
  const features = resolveChasubleFeatures(source);
  const gothicType = features?.gothic ? item.gothicType : null;
  const gothicMaterial = features?.gothic
    ? getChasubleMaterialField(features, item)
    : null;
  const hasFabric = hasChasublePanelFabric(gothicType);
  const hasDimension =
    gothicType === ChasubleGothicType.EmbroideredOrphrey ||
    gothicType === ChasubleGothicType.PlainWithTrimmings;
  const hasWidth =
    features?.panelWidth !== false || (print && features.panel === false);
  const fields: (ChasublePanelField | false | undefined)[] = [
    features?.gothic && 'gothicType',
    gothicMaterial === 'galoon' && 'galoon',
    hasFabric && 'fabric',
    hasFabric && 'color',
    features?.patching && 'patching',
    hasDimension && 'dimension',
    hasWidth && 'width',
    'length',
    features?.roman && 'stoleSize',
    gothicMaterial === 'trimming' && 'trimming',
    features?.roman && 'liningFabric',
    features?.roman && 'liningColor',
  ];
  return fields.filter((field): field is ChasublePanelField => !!field);
}

export const BODY_TYPE_OPTIONS = getOptions(ChasubleBodyType);

export function getBodyTypeLabel(bodyType?: string | null): string | null {
  return (
    BODY_TYPE_OPTIONS.find((option) => option.value === bodyType)?.label ?? null
  );
}

/** Whether the item's body type takes a sleeve design, saved as its design. */
export function hasChasubleSleeveDesign(
  vestmentType: VestmentType | null | undefined,
  bodyType?: string | null
): boolean {
  return (
    !!getChasubleFeatures(vestmentType)?.bodyType &&
    bodyType === ChasubleBodyType.WithSleeveEmbroidery
  );
}

/** The item's design code, or its type's default design code when it has none. */
export function getItemDesignCode(item: {
  vestment?: { type: VestmentType } | null;
  design?: { code: string } | null;
}): string | null {
  return (
    item.design?.code ||
    getChasubleFeatures(item.vestment?.type)?.defaultDesignCode ||
    null
  );
}
